import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Chip, Divider, Alert, LinearProgress, Tooltip, IconButton,
} from '@mui/material';
import { Close, CheckCircleOutline, HistoryToggleOff, Undo } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useCapabilities } from '../contexts/CapabilitiesContext';

const API = process.env.REACT_APP_API_ENDPOINT || '';

/** Matches AcknowledgeWarningPanel. A Tooltip's popper defaults to 1500 — BELOW this dialog — so every
    tooltip inside has to be lifted explicitly or it renders behind the dialog it belongs to. */
const ACK_DIALOG_Z = 2200;
const POPPER_ABOVE_DIALOG = { popper: { sx: { zIndex: ACK_DIALOG_Z + 100 } } };

/** One row of the MeterAcknowledgements table (see acknowledge_warnings in meter_maintenance.py). */
interface AckRow {
  meter_product_id: string;
  acknowledged_at: string;
  acknowledged_by?: string;
  reason_code?: string;
  reason_label?: string;
  note?: string;
  reasons?: string[];
  expires_at?: string;
  status?: 'active' | 'expired' | 'revoked';
  expiry_reason?: string;
  revoked_by?: string;
  revoked_at?: string;
  consecutive?: number;
}

interface Props {
  open: boolean;
  meterProductId: string;
  trackLabel?: string;        // e.g. "BAY 11 POS 4 — KEROSENE HI"
  onClose: () => void;
  onChanged?: () => void;     // a revoke changes health -> parent refetches in place
}

/** Why the acknowledgement ended, in words. Codes are set by the sweeper and the aggregator. */
const EXPIRY_TEXT: Record<string, string> = {
  ttl: 'Reached its 30-day limit',
  new_proving: 'A new proving was recorded',
  maintenance: 'Maintenance was recorded',
  reason_changed: 'The warning reason changed',
  escalated: 'The track escalated to Critical',
  resolved: 'The warning cleared on its own',
  revoked: 'Revoked by a user',
};

const fmt = (iso?: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const daysLeft = (iso?: string) => {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / 86400000));
};

/**
 * Acknowledgement history for one meter-product track (ACKNOWLEDGE_WARNINGS_PLAN.md decision 14).
 *
 * Read-only for everyone — the audit trail is the whole point of the feature, so it is never gated.
 * Revoking the ACTIVE acknowledgement needs run_maintenance, the same capability that granted it.
 */
const AcknowledgementHistory: React.FC<Props> = ({ open, meterProductId, trackLabel, onClose, onChanged }) => {
  const { getIdTokenClaims } = useAuth0();
  const { can } = useCapabilities();
  const mayRevoke = can('run_maintenance');

  const [rows, setRows] = useState<AckRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const res = await fetch(`${API}/meters/${encodeURIComponent(meterProductId)}/acknowledgements`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Could not load history (${res.status})`);
      const data = await res.json();
      setRows(data.acknowledgements || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [getIdTokenClaims, meterProductId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const revoke = async () => {
    setBusy(true); setError(null);
    try {
      const token = (await getIdTokenClaims())?.__raw;
      const res = await fetch(`${API}/meters/${encodeURIComponent(meterProductId)}/acknowledge`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Could not revoke (${res.status})`);
      await load();
      onChanged?.();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const active = rows.find(r => (r.status || 'active') === 'active');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: ACK_DIALOG_Z }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <HistoryToggleOff fontSize="small" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>Acknowledgement history</Typography>
          <Typography variant="caption" color="text.secondary">{trackLabel || meterProductId}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 12 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {(loading || busy) && <LinearProgress sx={{ mb: 1.5 }} />}
        {error && <Alert severity="warning" sx={{ mb: 1.5 }}>{error}</Alert>}

        {!loading && !rows.length && (
          <Typography variant="body2" color="text.secondary">
            This track has never been acknowledged.
          </Typography>
        )}

        {rows.map((r, i) => {
          const status = r.status || 'active';
          const isActive = status === 'active';
          const left = daysLeft(r.expires_at);
          return (
            <Box key={`${r.acknowledged_at}-${i}`} sx={{ mb: 2 }}>
              {i > 0 && <Divider sx={{ mb: 2 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                <Chip
                  size="small"
                  icon={isActive ? <CheckCircleOutline fontSize="small" /> : undefined}
                  color={isActive ? 'info' : 'default'}
                  variant={isActive ? 'filled' : 'outlined'}
                  label={isActive ? 'Active' : status === 'revoked' ? 'Revoked' : 'Expired'}
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {r.reason_label || r.reason_code || 'Acknowledged'}
                </Typography>
                {(r.consecutive ?? 1) > 1 && (
                  <Tooltip slotProps={POPPER_ABOVE_DIALOG}
                           title={`This track has now been acknowledged ${r.consecutive} times in a row — a repeatedly parked warning may need a real fix.`}>
                    <Chip size="small" color="warning" variant="outlined"
                          label={`×${r.consecutive} in a row`} sx={{ height: 22, fontSize: '0.7rem' }} />
                  </Tooltip>
                )}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                By {r.acknowledged_by || 'unknown'} on {fmt(r.acknowledged_at)}
              </Typography>

              {isActive ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Returns automatically {left != null ? `in ${left} day${left === 1 ? '' : 's'} ` : ''}({fmt(r.expires_at)}),
                  or sooner on a new proving, maintenance, or a change of reason.
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Ended: {EXPIRY_TEXT[r.expiry_reason || ''] || r.expiry_reason || 'unknown'}
                  {r.revoked_by ? ` — by ${r.revoked_by} on ${fmt(r.revoked_at)}` : ''}
                </Typography>
              )}

              {r.note && (
                <Typography variant="body2" sx={{ mt: 0.75, fontStyle: 'italic', color: 'text.secondary' }}>
                  “{r.note}”
                </Typography>
              )}

              {!!(r.reasons && r.reasons.length) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Warning at the time: {r.reasons.join(' · ')}
                </Typography>
              )}
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions>
        {active && (
          <Tooltip slotProps={POPPER_ABOVE_DIALOG} title={mayRevoke
            ? 'Bring this warning back now, before it expires on its own.'
            : 'Revoking an acknowledgement requires maintenance permissions.'}>
            <span>
              <Button color="warning" startIcon={<Undo />} disabled={!mayRevoke || busy} onClick={revoke}>
                Revoke
              </Button>
            </span>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AcknowledgementHistory;
